import {AsyncError} from 'Core/models/Async';
import {eqDefault} from 'Core/models/Default';
import {eqValidatableEmail, ValidatableEmail, ValidatedEmail} from 'Core/models/Email';
import {eqStringUnion, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {
  eqValidatableNonEmptyString,
  ValidatableNonEmptyString,
  ValidatedNonEmptyString,
} from 'Core/models/ValidatableNonEmptyString';
import {eqValidatablePhone, isEmptyPhoneNumber, OptionalPhoneNumber, ValidatablePhone} from 'Core/models/PhoneNumber';
import {eqValid} from 'Core/models/Valid';
import {isValid} from 'Core/models/Validatable';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import * as E from 'fp-ts/lib/Either';
import {Eq, eqBoolean, eqString, getStructEq} from 'fp-ts/lib/Eq';
import {
  eqRecruiterCode,
  isValidRecruiterCode,
  RecruiterCode,
  ValidRecruiterCode,
} from 'Agent/Policies/models/RecruiterCode';

export class UnvalidatedFieldSalesLeader {
  public readonly key = 'UnvalidatedFieldSalesLeader';

  constructor(
    public readonly firstName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly username: ValidatableNonEmptyString,
    public readonly phoneNumber: ValidatablePhone,
    public readonly email: ValidatableEmail,
    public readonly recruiterCode: RecruiterCode,
    public readonly isNewYork: boolean,
    public readonly isAhl: boolean
  ) {}
}

export class InvalidFieldSalesLeader {
  public readonly key = 'InvalidFieldSalesLeader';

  public constructor(
    public readonly firstName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly username: ValidatableNonEmptyString,
    public readonly phoneNumber: ValidatablePhone,
    public readonly email: ValidatableEmail,
    public readonly recruiterCode: RecruiterCode,
    public readonly isNewYork: boolean,
    public readonly isAhl: boolean
  ) {}
}

export class ValidFieldSalesLeader {
  public static create(
    firstName: StringUnion,
    lastName: StringUnion,
    username: ValidatableNonEmptyString,
    phoneNumber: ValidatablePhone,
    email: ValidatableEmail,
    recruiterCode: RecruiterCode,
    isNewYork: boolean,
    isAhl: boolean
  ) {
    return firstName instanceof NonEmptyString &&
      lastName instanceof NonEmptyString &&
      isValid(username) &&
      (isValid(phoneNumber) || isEmptyPhoneNumber(phoneNumber)) &&
      isValid(email) &&
      isValidRecruiterCode(recruiterCode) &&
      (isAhl || isNewYork)
      ? new ValidFieldSalesLeader(firstName, lastName, username, phoneNumber, email, recruiterCode, isNewYork, isAhl)
      : new InvalidFieldSalesLeader(firstName, lastName, username, phoneNumber, email, recruiterCode, isNewYork, isAhl);
  }

  public readonly key = 'ValidFieldSalesLeader';
  private constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly username: ValidatedNonEmptyString,
    public readonly phoneNumber: OptionalPhoneNumber,
    public readonly email: ValidatedEmail,
    public readonly recruiterCode: E.Right<ValidRecruiterCode>,
    public readonly isNewYork: boolean,
    public readonly isAhl: boolean
  ) {}
}

export class ErrorFieldSalesLeader {
  public readonly key = 'ErrorFieldSalesLeader';

  public constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly username: ValidatedNonEmptyString,
    public readonly phoneNumber: OptionalPhoneNumber,
    public readonly email: ValidatedEmail,
    public readonly recruiterCode: E.Right<ValidRecruiterCode>,
    public readonly isNewYork: boolean,
    public readonly isAhl: boolean,
    public readonly errors: AsyncError
  ) {}
}

export class SavedFieldSalesLeader {
  public readonly key = 'SavedFieldSalesLeader';

  public constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly username: ValidatedNonEmptyString,
    public readonly phoneNumber: OptionalPhoneNumber,
    public readonly email: ValidatedEmail,
    public readonly recruiterCode: E.Right<ValidRecruiterCode>,
    public readonly isNewYork: boolean,
    public readonly isAhl: boolean,
    public readonly id: string
  ) {}
}

export type FieldSalesLeader =
  | ErrorFieldSalesLeader
  | SavedFieldSalesLeader
  | ValidFieldSalesLeader
  | InvalidFieldSalesLeader
  | UnvalidatedFieldSalesLeader;

export type FieldSalesLeaderOptions<TReturn> = ToMatchOptions<FieldSalesLeader, 'key', TReturn>;

export const matchFieldSalesLeader = <TReturn>(
  fsl: FieldSalesLeader,
  options: FieldSalesLeaderOptions<TReturn>
): TReturn => {
  switch (fsl.key) {
    case 'InvalidFieldSalesLeader':
      return options.InvalidFieldSalesLeader(fsl);
    case 'UnvalidatedFieldSalesLeader':
      return options.UnvalidatedFieldSalesLeader(fsl);
    case 'ValidFieldSalesLeader':
      return options.ValidFieldSalesLeader(fsl);
    case 'SavedFieldSalesLeader':
      return options.SavedFieldSalesLeader(fsl);
    case 'ErrorFieldSalesLeader':
      return options.ErrorFieldSalesLeader(fsl);
  }
};

export const eqFieldSalesLeader: Eq<FieldSalesLeader> = getStructEq<FieldSalesLeader>({
  email: eqValidatableEmail,
  firstName: eqStringUnion,
  key: eqString,
  lastName: eqStringUnion,
  phoneNumber: eqValidatablePhone,
  recruiterCode: eqValid(eqDefault(eqRecruiterCode), eqRecruiterCode),
  username: eqValidatableNonEmptyString,
  isAhl: eqBoolean,
  isNewYork: eqBoolean,
});

export const validateFieldSalesLeader = (fsl: FieldSalesLeader) =>
  ValidFieldSalesLeader.create(
    fsl.firstName,
    fsl.lastName,
    fsl.username,
    fsl.phoneNumber,
    fsl.email,
    fsl.recruiterCode,
    fsl.isNewYork,
    fsl.isAhl
  );
